import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import { CtaLink } from '@/components/generals/cta-link'
import { Mark } from '@/components/generals/mark'
import { SectionLabel } from '@/components/generals/section-label'
import ProjectGrid from '@/components/sections/project-grid'

interface SelectedWorkProps {
    projects: ProjectSummary[]
}

const SelectedWork: FC<SelectedWorkProps> = ({ projects }): ReactNode => {
    const hasConcepts = projects.some((project) => project.isConcept)

    return (
        <section id='work' aria-labelledby='work-title' data-nav-theme='dark' className='bg-charcoal text-cream'>
            <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                <div className='grid gap-10 lg:grid-cols-12 lg:items-end'>
                    <div data-reveal className='lg:col-span-7'>
                        <SectionLabel tone='dark'>Selected work / 02</SectionLabel>
                        <h2 id='work-title' className='mt-6 font-heading type-section'>
                            <span className='md:block'>Ideas that make</span>{' '}
                            <span className='md:block'><Mark tone='dark'>an impact.</Mark></span>
                        </h2>
                    </div>
                    <div data-reveal className='space-y-4 lg:col-span-4 lg:col-start-9'>
                        <p className='text-[17px] leading-relaxed text-cream/80'>
                            We develop creative campaigns and strategic marketing experiences that help ambitious brands connect with audiences and grow.
                        </p>
                        {hasConcepts && (
                            <p className='font-mono text-[11px] uppercase leading-relaxed tracking-[0.16em] text-cream/60'>
                                Projects marked concept are speculative work that shows our approach.
                            </p>
                        )}
                    </div>
                </div>

                {projects.length > 0 ? (
                    <ProjectGrid projects={projects} className='mt-14 md:mt-20' />
                ) : (
                    <p className='mt-16 text-cream/70'>New projects are on their way.</p>
                )}

                <div data-reveal className='mt-20 flex flex-col items-start justify-between gap-6 border-t border-cream/15 pt-8 sm:flex-row sm:items-center'>
                    <p className='font-heading font-expanded text-xl font-bold md:text-2xl'>Want to see the whole portfolio?</p>
                    <CtaLink href='/work' variant='primary-dark'>Explore all projects</CtaLink>
                </div>
            </div>
        </section>
    )
}

export default SelectedWork
